import { useEffect, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useT } from '../../../messages';

/**
 * A dialog that holds a tool rather than a question — the offer simulator,
 * the booking rule's form, the offboarding check.
 *
 * `Confirm` (bits.tsx) is the question-shaped one and stays that; this is its
 * sibling for content, with the same four behaviours so the two feel like one
 * console: focus starts inside, Tab cycles within, Esc closes (unless `busy` —
 * closing then would hide a write that is still going to land), and focus goes
 * back to whatever opened it. A `Confirm` opened from inside one of these
 * handles its own Esc first and stops it there, so Esc answers the innermost
 * question.
 *
 * `size="large"` is the simulator's: three columns of times want the width.
 */
export function Dialog({
  title,
  size = 'medium',
  busy = false,
  onClose,
  children,
  footer,
}: {
  title: string;
  size?: 'medium' | 'large';
  busy?: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}): ReactNode {
  const t = useT();
  const panel = useRef<HTMLDivElement>(null);
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current
      ?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), button')
      ?.focus();
    return () => {
      if (opener !== null && opener.isConnected) opener.focus();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!latest.current.busy) latest.current.onClose();
      return;
    }
    if (e.key !== 'Tab' || panel.current === null) return;
    const focusable = [
      ...panel.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      ),
    ].filter(
      (el) =>
        panel.current?.contains(el) === true &&
        el.closest('.modal') === panel.current?.parentElement,
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="modal">
      <div
        ref={panel}
        className={`modal__panel setup-dialog setup-dialog--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={onKeyDown}
      >
        <header className="setup-dialog__head">
          <h3>{title}</h3>
          <button
            type="button"
            className="button button--quiet button--sm"
            onClick={onClose}
            disabled={busy}
          >
            {t('setup.dialog.close')}
          </button>
        </header>
        <div className="setup-dialog__body">{children}</div>
        {footer === undefined ? null : <div className="modal__actions">{footer}</div>}
      </div>
    </div>
  );
}
